import React, { useState, useEffect, useMemo } from 'react';
import { format, subDays, startOfDay, endOfDay,
         startOfMonth, endOfMonth, isWithinInterval, eachDayOfInterval } from 'date-fns';
import { el } from 'date-fns/locale';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { bookingApi, serviceApi, staffApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import './StatsPage.css';

const RANGE_OPTIONS = [
  { id: '7d',    label: '7 μέρες' },
  { id: '30d',   label: '30 μέρες' },
  { id: 'month', label: 'Τρέχων μήνας' },
  { id: '90d',   label: '3 μήνες' },
];

const STATUS_COLORS = {
  confirmed: '#534AB7', completed: '#2ECC71',
  cancelled: '#E74C3C', pending:   '#F39C12', no_show: '#9B59B6'
};
const STATUS_LABELS = {
  confirmed: 'Επιβεβαιωμένες', completed: 'Ολοκληρώθηκαν',
  cancelled: 'Ακυρώθηκαν',     pending:   'Εκκρεμείς', no_show: 'Δεν ήρθαν'
};
const CHART_COLORS = [
  '#534AB7','#E74C3C','#2ECC71','#F39C12',
  '#3498DB','#9B59B6','#E91E63','#00BCD4',
  '#FF5722','#607D8B','#16A085','#8E44AD',
];

const fmtEur = (v) => v != null ? `${v.toFixed(2)} €` : '—';
const fmtPct = (v) => `${v.toFixed(1)}%`;

// ── Υπολογισμός αξίας κράτησης: discount παρακάμπτει service.price ──
const totalValue = (b) => {
  const price = b.discount != null && parseFloat(b.discount) > 0
    ? parseFloat(b.discount)
    : (parseFloat(b.service?.price) || 0);
  return price + (parseFloat(b.tip) || 0);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="st-tooltip">
      {label && <div className="st-tooltip-label">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="st-tooltip-row">
          <span className="st-tooltip-dot" style={{ background: p.color || p.fill }} />
          <span>{p.name}: <b>{p.value}</b></span>
        </div>
      ))}
    </div>
  );
};

export default function StatsPage() {
  const { openSidebar } = useAppLayout();
  const [range, setRange]       = useState('30d');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);

  const { from, to } = useMemo(() => {
    const now = new Date();
    if (range === '7d')    return { from: subDays(now, 7),   to: now };
    if (range === '30d')   return { from: subDays(now, 30),  to: now };
    if (range === 'month') return { from: startOfMonth(now), to: endOfMonth(now) };
    if (range === '90d')   return { from: subDays(now, 90),  to: now };
    return { from: subDays(now, 30), to: now };
  }, [range]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      bookingApi.getAll(from.toISOString(), to.toISOString()),
      serviceApi.getAll(),
      staffApi.getAll(),
    ]).then(([bRes]) => {
      setBookings(bRes.data.bookings || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = useMemo(() =>
    bookings.filter(b => b.status !== 'cancelled' && b.status !== 'no_show'),
  [bookings]);

  const kpis = useMemo(() => {
    const total     = bookings.length;
    const done      = bookings.filter(b => b.status === 'completed').length;
    const cancelled = bookings.filter(b => b.status === 'cancelled').length;
    const revenue   = active.reduce((s, b) => s + totalValue(b), 0);
    const totalTips = active.reduce((s, b) => s + (parseFloat(b.tip) || 0), 0);
    const avgDur    = active.length
      ? Math.round(active.reduce((s, b) => s + b.service_duration, 0) / active.length) : 0;
    const uniqueClients = new Set(active.map(b => b.client_phone || b.client_name)).size;
    const cancelRate    = total ? (cancelled / total) * 100 : 0;
    return { total, done, cancelled, revenue, totalTips, avgDur, uniqueClients, cancelRate };
  }, [bookings, active]);

  const perDay = useMemo(() => {
    const days = eachDayOfInterval({ start: startOfDay(from), end: startOfDay(to) });
    return days.map(day => {
      const label   = format(day, range === '90d' ? 'd/M' : 'EEE d/M', { locale: el });
      const inDay   = (b) => isWithinInterval(new Date(b.starts_at), { start: startOfDay(day), end: endOfDay(day) });
      const count   = active.filter(inDay).length;
      const revenue = active.filter(inDay).reduce((s, b) => s + totalValue(b), 0);
      return { label, count, revenue: parseFloat(revenue.toFixed(2)) };
    });
  }, [active, from, to, range]);

  const perService = useMemo(() => {
    const map = {};
    active.forEach(b => {
      const name = b.service_name || 'Άλλο';
      if (!map[name]) map[name] = { name, count: 0, revenue: 0, color: b.service?.color || '#999' };
      map[name].count++;
      map[name].revenue += totalValue(b);
    });
    return Object.values(map)
      .sort((a, b) => b.count - a.count)
      .map((s, i) => ({ ...s, color: s.color !== '#999' ? s.color : CHART_COLORS[i % CHART_COLORS.length], revenue: parseFloat(s.revenue.toFixed(2)) }));
  }, [active]);

  const perStaff = useMemo(() => {
    const map = {};
    active.forEach(b => {
      const name = b.staff?.name || 'Χωρίς υπάλληλο';
      if (!map[name]) map[name] = { name, count: 0, revenue: 0, color: b.staff?.color || '#999' };
      map[name].count++;
      map[name].revenue += totalValue(b);
    });
    return Object.values(map).sort((a, b) => b.count - a.count)
      .map(s => ({ ...s, revenue: parseFloat(s.revenue.toFixed(2)) }));
  }, [active]);

  const DAY_NAMES = ['Κυρ','Δευ','Τρι','Τετ','Πεμ','Παρ','Σαβ'];
  const perWeekday = useMemo(() => {
    const map = Array(7).fill(0).map((_, i) => ({ label: DAY_NAMES[i], count: 0 }));
    active.forEach(b => { map[new Date(b.starts_at).getDay()].count++; });
    return [map[1],map[2],map[3],map[4],map[5],map[6],map[0]];
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusBreakdown = useMemo(() =>
    Object.entries(
      bookings.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {})
    ).map(([status, count]) => ({
      name: STATUS_LABELS[status] || status,
      value: count,
      color: STATUS_COLORS[status] || '#999'
    })),
  [bookings]);

  const perHour = useMemo(() => {
    const map = {};
    active.forEach(b => { const h = new Date(b.starts_at).getHours(); map[h] = (map[h] || 0) + 1; });
    return Array.from({ length: 13 }, (_, i) => i + 8).map(h => ({ label: `${h}:00`, count: map[h] || 0 }));
  }, [active]);

  return (
    <div className="st-page">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="st-header">
        <div className="st-header-top">
          <div className="st-header-left">
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="st-title">📊 Στατιστικά</h1>
          </div>
        </div>

        <div className="st-range-tabs">
          {RANGE_OPTIONS.map(r => (
            <button key={r.id}
              className={'st-range-btn' + (range === r.id ? ' active' : '')}
              onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="st-period-label">
          {format(from, 'd MMM yyyy', { locale: el })} – {format(to, 'd MMM yyyy', { locale: el })}
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="st-content">
        {loading && (
          <div className="st-loading"><div className="st-spinner" /><p>Φόρτωση στατιστικών...</p></div>
        )}

        {!loading && (
          <>
            {/* KPIs */}
            <div className="st-kpi-grid">
              {[
                { icon: '📋', val: kpis.total,                          lbl: 'Συνολικές κρατήσεις' },
                { icon: '✅', val: kpis.done,                           lbl: 'Ολοκληρώθηκαν' },
                { icon: '💶', val: fmtEur(kpis.revenue),                lbl: 'Συνολικά έσοδα',   accent: '#2ECC71' },
                ...(kpis.totalTips > 0 ? [{ icon: '💰', val: fmtEur(kpis.totalTips), lbl: 'Φιλοδωρήματα', accent: '#16a34a' }] : []),
                { icon: '👥', val: kpis.uniqueClients,                  lbl: 'Μοναδικοί πελάτες' },
                { icon: '🕐', val: `${kpis.avgDur} λεπτά`,             lbl: 'Μέση διάρκεια' },
                { icon: '❌', val: fmtPct(kpis.cancelRate),             lbl: 'Ποσοστό ακύρωσης', accent: kpis.cancelRate > 20 ? '#E74C3C' : '#534AB7' },
              ].map((k, i) => (
                <div key={i} className="st-kpi" style={k.accent ? { '--kpi-accent': k.accent } : {}}>
                  <div className="st-kpi-icon">{k.icon}</div>
                  <div className="st-kpi-val">{k.val}</div>
                  <div className="st-kpi-lbl">{k.lbl}</div>
                </div>
              ))}
            </div>

            {/* Κρατήσεις ανά ημέρα */}
            <div className="st-card">
              <div className="st-card-title">📅 Κρατήσεις ανά ημέρα</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={perDay} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                    interval={range === '90d' ? 6 : range === '30d' ? 3 : 0} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Κρατήσεις" fill="#534AB7" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Έσοδα ανά ημέρα */}
            {kpis.revenue > 0 && (
              <div className="st-card">
                <div className="st-card-title">💶 Έσοδα ανά ημέρα</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={perDay} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                      interval={range === '90d' ? 6 : range === '30d' ? 3 : 0} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip content={<CustomTooltip />} formatter={(v) => [`${v} €`, 'Έσοδα']} />
                    <Line type="monotone" dataKey="revenue" name="Έσοδα (€)" stroke="#2ECC71" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ανά υπηρεσία + υπάλληλο */}
            <div className="st-two-col">
              {perService.length > 0 && (
                <div className="st-card">
                  <div className="st-card-title">✂️ Ανά υπηρεσία</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={perService} dataKey="count" nameKey="name"
                        cx="50%" cy="50%" outerRadius={75}
                        label={({ name, percent }) => `${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {perService.map((s, i) => <Cell key={i} fill={s.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="st-legend">
                    {perService.map((s, i) => (
                      <div key={i} className="st-legend-row">
                        <span className="st-legend-dot" style={{ background: s.color }} />
                        <span className="st-legend-name">{s.name}</span>
                        <span className="st-legend-val">{s.count}</span>
                        {s.revenue > 0 && <span className="st-legend-rev">{fmtEur(s.revenue)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {perStaff.length > 0 && (
                <div className="st-card">
                  <div className="st-card-title">👤 Ανά υπάλληλο</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={perStaff} layout="vertical" margin={{ top: 0, right: 8, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-primary)' }} width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Κρατήσεις" radius={[0,3,3,0]}>
                        {perStaff.map((s, i) => <Cell key={i} fill={s.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="st-legend">
                    {perStaff.map((s, i) => (
                      <div key={i} className="st-legend-row">
                        <span className="st-legend-dot" style={{ background: s.color }} />
                        <span className="st-legend-name">{s.name}</span>
                        <span className="st-legend-val">{s.count}</span>
                        {s.revenue > 0 && <span className="st-legend-rev">{fmtEur(s.revenue)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Ανά ημέρα εβδομάδας + ώρα */}
            <div className="st-two-col">
              <div className="st-card">
                <div className="st-card-title">📆 Ανά ημέρα εβδομάδας</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={perWeekday} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Κρατήσεις" fill="#3498DB" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="st-card">
                <div className="st-card-title">🕐 Ανά ώρα</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={perHour} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Κρατήσεις" fill="#9B59B6" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Status breakdown */}
            {statusBreakdown.length > 0 && (
              <div className="st-card">
                <div className="st-card-title">📊 Κατάσταση κρατήσεων</div>
                <div className="st-status-grid">
                  {statusBreakdown.map((s, i) => (
                    <div key={i} className="st-status-item">
                      <div className="st-status-bar-wrap">
                        <div className="st-status-bar"
                          style={{ width: `${(s.value / bookings.length * 100).toFixed(0)}%`, background: s.color }} />
                      </div>
                      <div className="st-status-info">
                        <span className="st-status-dot" style={{ background: s.color }} />
                        <span className="st-status-name">{s.name}</span>
                        <span className="st-status-count">{s.value}</span>
                        <span className="st-status-pct">{fmtPct(s.value / bookings.length * 100)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {active.length === 0 && (
              <div className="st-empty">
                <div className="st-empty-icon">📊</div>
                <p className="st-empty-title">Δεν υπάρχουν δεδομένα για αυτή την περίοδο</p>
                <p className="st-empty-sub">Δοκίμασε διαφορετικό χρονικό εύρος</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}